import { useState, useMemo } from 'react';
import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import SoftTypography from 'components/SoftTypography';
import DataTableCustom from 'components/Tables/DataTableCustom';
import { Icon } from '@mui/material';

function PaginatedExpandedRow({ items, columns, rowId, pageSize = 50 }) {
    const [currentPage, setCurrentPage] = useState(1);

    // Calculate pagination values
    const totalItems = items.length;
    const totalPages = Math.ceil(totalItems / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalItems);

    // Get current page items
    const currentItems = useMemo(() => {
        return items
            .slice(startIndex, endIndex)
            .map((item, idx) => ({
                ...item,
                id: `${rowId}-item-${startIndex + idx}`
            }));
    }, [items, startIndex, endIndex, rowId]);

    // Navigation handlers
    const handleFirstPage = () => setCurrentPage(1);
    const handlePreviousPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
    const handleNextPage = () => setCurrentPage(prev => Math.min(totalPages, prev + 1));
    const handleLastPage = () => setCurrentPage(totalPages);

    // If items are few, render without pagination
    if (totalItems <= pageSize) {
        return (
            <SoftBox px={0.5} py={0.5} width="100%" sx={{ border: '1px solid #bec4c0ff', borderRadius: '8px', my: 1 }}>
                <DataTableCustom
                    columns={columns}
                    data={items.map((item, idx) => ({ ...item, id: `${rowId}-item-${idx}` }))}
                    showActions={false}
                    zebraStripes={true}
                    border={false}
                    showFooter={false}
                    headerStyles={{
                        backgroundColor: '#fff',
                        borderBottom: '1px solid #e0e0e0',
                        color: '#344767',
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                    }}
                />
            </SoftBox>
        );
    }

    // Render with pagination controls
    return (
        <SoftBox px={0.5} py={0.5} width="100%" sx={{ border: '1px solid #bec4c0ff', borderRadius: '8px', my: 1 }}>
            {/* Pagination Info and Controls - Top */}
            <SoftBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                px={2}
                py={1}
                sx={{
                    backgroundColor: '#fff',
                    borderBottom: '1px solid #e0e0e0'
                }}
            >
                <SoftTypography variant="caption" color="text" fontWeight="medium">
                    Mostrando {startIndex + 1} - {endIndex} de {totalItems} registros
                </SoftTypography>

                <SoftBox display="flex" gap={1} alignItems="center">
                    <SoftButton
                        variant="outlined"
                        color="dark"
                        size="small"
                        onClick={handleFirstPage}
                        disabled={currentPage === 1}
                        iconOnly
                        sx={{ minWidth: '32px', height: '32px', padding: '4px' }}
                    >
                        <Icon fontSize="small">first_page</Icon>
                    </SoftButton>

                    <SoftButton
                        variant="outlined"
                        color="dark"
                        size="small"
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        iconOnly
                        sx={{ minWidth: '32px', height: '32px', padding: '4px' }}
                    >
                        <Icon fontSize="small">chevron_left</Icon>
                    </SoftButton>

                    <SoftTypography variant="button" color="dark" fontWeight="medium" px={1}>
                        {currentPage} / {totalPages}
                    </SoftTypography>

                    <SoftButton
                        variant="outlined"
                        color="dark"
                        size="small"
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        iconOnly
                        sx={{ minWidth: '32px', height: '32px', padding: '4px' }}
                    >
                        <Icon fontSize="small">chevron_right</Icon>
                    </SoftButton>

                    <SoftButton
                        variant="outlined"
                        color="dark"
                        size="small"
                        onClick={handleLastPage}
                        disabled={currentPage === totalPages}
                        iconOnly
                        sx={{ minWidth: '32px', height: '32px', padding: '4px' }}
                    >
                        <Icon fontSize="small">last_page</Icon>
                    </SoftButton>
                </SoftBox>
            </SoftBox>

            {/* Data Table */}
            <DataTableCustom
                columns={columns}
                data={currentItems}
                showActions={false}
                zebraStripes={true}
                border={false}
                showFooter={false}
                headerStyles={{
                    backgroundColor: '#fff',
                    borderBottom: '1px solid #e0e0e0',
                    color: '#344767',
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                }}
            />

            {/* Pagination Info - Bottom */}
            <SoftBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                px={2}
                py={1}
                sx={{
                    backgroundColor: '#fff',
                    borderTop: '1px solid #e0e0e0'
                }}
            >
                <SoftTypography variant="caption" color="text" fontWeight="medium">
                    Página {currentPage} de {totalPages}
                </SoftTypography>
            </SoftBox>
        </SoftBox>
    );
}

export default PaginatedExpandedRow;
